import { useCallback, useEffect, useRef } from 'react';
import { getFrequenciesForChord, getMidiNotesForChord } from '../utils/chordAudio';

const DEFAULT_SAMPLE_BASE_URL = '/audio/salamander/Samples';
const DEFAULT_SAMPLE_VELOCITY = 8;
const SAMPLE_POINTS = [
  { midi: 21, note: 'A0' },
  { midi: 24, note: 'C1' },
  { midi: 27, note: 'D#1' },
  { midi: 30, note: 'F#1' },
  { midi: 33, note: 'A1' },
  { midi: 36, note: 'C2' },
  { midi: 39, note: 'D#2' },
  { midi: 42, note: 'F#2' },
  { midi: 45, note: 'A2' },
  { midi: 48, note: 'C3' },
  { midi: 51, note: 'D#3' },
  { midi: 54, note: 'F#3' },
  { midi: 57, note: 'A3' },
  { midi: 60, note: 'C4' },
  { midi: 63, note: 'D#4' },
  { midi: 66, note: 'F#4' },
  { midi: 69, note: 'A4' },
  { midi: 72, note: 'C5' },
  { midi: 75, note: 'D#5' },
  { midi: 78, note: 'F#5' },
  { midi: 81, note: 'A5' },
  { midi: 84, note: 'C6' },
  { midi: 87, note: 'D#6' },
  { midi: 90, note: 'F#6' },
  { midi: 93, note: 'A6' },
  { midi: 96, note: 'C7' },
  { midi: 99, note: 'D#7' },
  { midi: 102, note: 'F#7' },
  { midi: 105, note: 'A7' },
  { midi: 108, note: 'C8' },
];

const findNearestSample = (midi) =>
  SAMPLE_POINTS.reduce((closest, point) => {
    if (!closest) return point;
    const currentDistance = Math.abs(point.midi - midi);
    const closestDistance = Math.abs(closest.midi - midi);
    return currentDistance < closestDistance ? point : closest;
  }, null);

const buildSampleUrl = (baseUrl, note, velocity) =>
  `${baseUrl.replace(/\/$/, '')}/${encodeURIComponent(note)}v${velocity}.flac`;

export function useChordAudio(config = 0.5) {
  const options = typeof config === 'number' ? { initialVolume: config } : (config || {});
  const {
    initialVolume = 0.5,
    instrument = 'piano',
    sampleBaseUrl = DEFAULT_SAMPLE_BASE_URL,
    sampleVelocity = DEFAULT_SAMPLE_VELOCITY,
  } = options;

  const audioCtxRef = useRef(null);
  const activeNodesRef = useRef([]);
  const volumeRef = useRef(initialVolume);
  const instrumentRef = useRef(instrument);
  const sampleBaseUrlRef = useRef(sampleBaseUrl);
  const sampleVelocityRef = useRef(sampleVelocity);
  const sampleCacheRef = useRef(new Map());
  const sampleLoadingRef = useRef(new Map());
  const playIdRef = useRef(0);

  const stop = useCallback(() => {
    activeNodesRef.current.forEach(({ osc, source, gain }) => {
      try {
        const node = osc || source;
        if (!node) return;
        const ctx = node.context;
        const now = ctx.currentTime;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setTargetAtTime(0, now, 0.05);
        node.stop(now + 0.1);
      } catch (e) {
        // ignore
      }
    });
    activeNodesRef.current = [];
  }, []);

  useEffect(() => stop, [stop]);

  const ensureContext = () => {
    if (typeof window === 'undefined') return null;
    const AudioCtx =
      window.AudioContext || window.webkitAudioContext || null;
    if (!AudioCtx) return null;
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioCtx();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  };

  const setVolume = useCallback((value) => {
    const clamped = Math.min(1, Math.max(0, value));
    volumeRef.current = clamped;
  }, []);

  useEffect(() => {
    instrumentRef.current = instrument;
    sampleBaseUrlRef.current = sampleBaseUrl;
    sampleVelocityRef.current = sampleVelocity;
  }, [instrument, sampleBaseUrl, sampleVelocity]);

  const loadSampleBuffer = useCallback(async (ctx, url) => {
    if (sampleCacheRef.current.has(url)) {
      return sampleCacheRef.current.get(url);
    }
    if (sampleLoadingRef.current.has(url)) {
      return sampleLoadingRef.current.get(url);
    }
    const loadingPromise = fetch(url)
      .then((res) => res.arrayBuffer())
      .then((data) => ctx.decodeAudioData(data))
      .then((buffer) => {
        sampleCacheRef.current.set(url, buffer);
        sampleLoadingRef.current.delete(url);
        return buffer;
      })
      .catch((err) => {
        sampleLoadingRef.current.delete(url);
        throw err;
      });
    sampleLoadingRef.current.set(url, loadingPromise);
    return loadingPromise;
  }, []);

  const playChord = useCallback(
    (symbol) => {
      const ctx = ensureContext();
      if (!ctx) return;

      const instrumentMode = instrumentRef.current;
      playIdRef.current += 1;
      const playId = playIdRef.current;
      stop();

      if (instrumentMode === 'piano') {
        const midiNotes = getMidiNotesForChord(symbol);
        if (!midiNotes.length) return;
        const duration = 1.6;
        const velocity = sampleVelocityRef.current;
        const baseUrl = sampleBaseUrlRef.current;
        const notesToLoad = midiNotes
          .map((midi) => {
            const samplePoint = findNearestSample(midi);
            if (!samplePoint) return null;
            return {
              midi,
              samplePoint,
              url: buildSampleUrl(baseUrl, samplePoint.note, velocity),
            };
          })
          .filter(Boolean);

        Promise.all(
          notesToLoad.map(async ({ midi, samplePoint, url }) => {
            try {
              const buffer = await loadSampleBuffer(ctx, url);
              return { midi, samplePoint, buffer };
            } catch {
              return null;
            }
          })
        ).then((loadedNotes) => {
          if (playIdRef.current !== playId) return;
          const playableNotes = loadedNotes.filter(Boolean);
          if (!playableNotes.length) return;

          // Schedule every note against the same future start time to avoid staggered playback.
          const startAt = ctx.currentTime + 0.03;
          const stopAt = startAt + duration;

          playableNotes.forEach(({ midi, samplePoint, buffer }) => {
            const source = ctx.createBufferSource();
            const gain = ctx.createGain();
            source.buffer = buffer;
            source.playbackRate.value = Math.pow(2, (midi - samplePoint.midi) / 12);

            gain.gain.setValueAtTime(0, startAt);
            const peak = 0.8 * volumeRef.current;
            gain.gain.linearRampToValueAtTime(peak, startAt + 0.02);
            gain.gain.linearRampToValueAtTime(0, stopAt);

            source.connect(gain).connect(ctx.destination);
            source.start(startAt);
            source.stop(stopAt + 0.05);
            activeNodesRef.current.push({ source, gain });
          });
        });
        return;
      }

      const freqs = getFrequenciesForChord(symbol);
      if (!freqs.length) return;
      const now = ctx.currentTime;
      const duration = 1.2;

      freqs.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = index === 0 ? 'sine' : 'triangle';
        osc.frequency.value = freq;

        gain.gain.setValueAtTime(0, now);
        const peak = 0.4 * volumeRef.current;
        gain.gain.linearRampToValueAtTime(peak, now + 0.03);
        gain.gain.linearRampToValueAtTime(0, now + duration);

        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + duration + 0.05);

        activeNodesRef.current.push({ osc, gain });
      });
    },
    [loadSampleBuffer, stop]
  );

  return { playChord, stop, setVolume };
}
